/**
 * First-party session auth (ADR-0006). The console authenticates with a Redis-backed session
 * cookie — `createSalonomyClient` sends it via `credentials: 'include'`. We resolve the principal
 * once at boot with GET /v1/auth/me, then keep it in context. RBAC roles drive role-aware UI;
 * a chair_renter is a tenant boundary (their sealed book), not merely a permission set.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Membership, Role, SessionUser } from '@salonomy/types';
import { api, ApiClientError, unwrap } from './api.js';
import { queryClient } from './query.js';

export type AuthStatus = 'loading' | 'authed' | 'anon';

const FLOOR_ROLES: ReadonlySet<Role> = new Set(['owner', 'manager']);
const MANAGER_ROLES: ReadonlySet<Role> = new Set(['owner', 'manager']);

/**
 * Login outcome: either a full session OR a TOTP 2FA step-up challenge (the credentials were correct
 * but the account is enrolled, so no session is issued until the code is verified).
 */
export type LoginOutcome =
  | { kind: 'authed'; user: SessionUser }
  | { kind: 'twoFactor'; challengeId: string };

interface AuthApi {
  status: AuthStatus;
  user: SessionUser | null;
  /** True for owner/manager (can manage staff, services, settings, mint tokens). */
  isManager: boolean;
  /** True for owner/manager (sees the whole floor, incl. sealed renters' slots). */
  isFloorManager: boolean;
  login: (email: string, password: string, salonId?: string) => Promise<LoginOutcome>;
  /** Complete a 2FA step-up with a TOTP or recovery code → promotes the pending challenge to a session. */
  verifyTwoFactor: (
    challengeId: string,
    creds: { code?: string; recoveryCode?: string },
  ) => Promise<SessionUser>;
  logout: () => Promise<void>;
  switchSalon: (salonId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthApi | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<SessionUser | null>(null);

  const refresh = useCallback(async () => {
    try {
      const me = unwrap(await api.GET('/v1/auth/me'));
      setUser(me);
      setStatus('authed');
    } catch (err) {
      if (err instanceof ApiClientError && err.isAuth) {
        setUser(null);
        setStatus('anon');
      } else {
        // Network failure at boot: stay unauthenticated but don't crash the shell.
        setStatus('anon');
      }
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback<AuthApi['login']>(async (email, password, salonId) => {
    const result = unwrap(
      await api.POST('/v1/auth/login', {
        body: { email, password, ...(salonId ? { salonId } : {}) },
      }),
    );
    // Enrolled accounts return a step-up challenge instead of a session — surface it to the caller.
    if ('twoFactorRequired' in result) {
      return { kind: 'twoFactor' as const, challengeId: result.challengeId };
    }
    setUser(result);
    setStatus('authed');
    await queryClient.invalidateQueries();
    return { kind: 'authed' as const, user: result };
  }, []);

  const verifyTwoFactor = useCallback<AuthApi['verifyTwoFactor']>(async (challengeId, creds) => {
    const me = unwrap(
      await api.POST('/v1/auth/2fa/verify', {
        body: { challengeId, ...creds },
      }),
    );
    setUser(me);
    setStatus('authed');
    await queryClient.invalidateQueries();
    return me;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.POST('/v1/auth/logout');
    } finally {
      setUser(null);
      setStatus('anon');
      queryClient.clear();
    }
  }, []);

  const switchSalon = useCallback<AuthApi['switchSalon']>(async (salonId) => {
    const me = unwrap(await api.POST('/v1/auth/switch', { body: { salonId } }));
    setUser(me);
    queryClient.clear();
  }, []);

  const value = useMemo<AuthApi>(
    () => ({
      status,
      user,
      isManager: user ? MANAGER_ROLES.has(user.role) : false,
      isFloorManager: user ? FLOOR_ROLES.has(user.role) : false,
      login,
      verifyTwoFactor,
      logout,
      switchSalon,
      refresh,
    }),
    [status, user, login, verifyTwoFactor, logout, switchSalon, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthApi {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function useMemberships(): { data: Membership[] | undefined } {
  // Lightweight: memberships rarely change within a session; fetched lazily by the switcher.
  const [data, setData] = useState<Membership[] | undefined>(undefined);
  useEffect(() => {
    void (async () => {
      try {
        setData(unwrap(await api.GET('/v1/auth/memberships')));
      } catch {
        setData([]);
      }
    })();
  }, []);
  return { data };
}
